import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {BusinessLimit} from '@/types';
import * as formatDate from '@/utils/date';

import LimitSummary from './LimitSummary';
type ExtraRowArg = BusinessLimit;

const BusinessLimitsTable = ({
  items,
  businessId,
  dataCy,
}: {
  items: BusinessLimit[];
  businessId?: string;
  dataCy?: string;
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
}) => {
  return (
    <Table
      containerDataCy={dataCy ? `${dataCy}-table` : undefined}
      className="overflow-hidden"
    >
      <Thead>
        <Tr>
          <Th className="w-20">&nbsp;</Th>
          <Th>Name</Th>
          <Th>Status</Th>
          <Th>Date</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <BusinessLimitsRow
            key={item.id ?? index}
            item={item}
            index={index}
            businessId={businessId}
            dataCy={dataCy}
          />
        ))}
      </TBody>
    </Table>
  );
};

const BusinessLimitsRow = ({
  item,
  index,
  businessId,
  dataCy,
}: {
  item: BusinessLimit;
  index: number;
  businessId?: string;
  dataCy?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  return (
    <>
      <Tr className={zebraRow} data-cy={dataCy ? `${dataCy}-row` : undefined}>
        <Td isNumeric>
          <Button
            dataCy={dataCy ? `${dataCy}-expand` : undefined}
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-name` : undefined}>{item?.name}</Td>
        <Td data-cy={dataCy ? `${dataCy}-status` : undefined}>
          {!item?.meta?.flags?.isDeactivated ? 'ACTIVE' : 'INACTIVE'}
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-date` : undefined}>
          {formatDate.DDMonYYYY(String(item.updatedAt))}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr
          enableHover={false}
          data-cy={dataCy ? `${dataCy}-summary-row` : undefined}
        >
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <LimitSummary
                item={item}
                businessId={businessId}
                dataCy={dataCy ? `${dataCy}-summary` : undefined}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default BusinessLimitsTable;
